import Analytics from '@lib/Analytics'
import ApiClient, { UsersABConfigType } from '@lib/ApiClient'
import Config from '@lib/Config'
import Log from '@lib/Log'

export type ABQuotesFlags = {
  medSupQuotesFlag: QuoteABTestType | null
  medAdvQuotesFlag: QuoteABTestType | null
  lifeQuotesFlag: QuoteABTestType | null
  pdpQuotesFlag: QuoteABTestType | null
}

class ABConfigs {
  medSupQuotesFlag: QuoteABTestType = 'off'
  medAdvQuotesFlag: QuoteABTestType = 'off'
  lifeQuotesFlag: QuoteABTestType = 'off'
  pdpQuotesFlag: QuoteABTestType = 'off'

  isMSOverwritten: boolean = false
  isMAOverwritten: boolean = false
  isLIOverwritten: boolean = false
  isPDPverwritten: boolean = false

  configs?: UsersABConfigType

  isFetched: boolean = false
  onSaveSessionData: (key: string, value: any) => void

  init (
    onSaveSessionData: (key: string, value: any) => void
  ) {
    this.onSaveSessionData = onSaveSessionData
  }

  presetQuoteConfigs = async (configs: ABQuotesFlags) => {
    if (configs.medSupQuotesFlag) {
      this.medSupQuotesFlag = configs.medSupQuotesFlag
      this.isMSOverwritten = true
    }
    if (configs.medAdvQuotesFlag) {
      this.medAdvQuotesFlag = configs.medAdvQuotesFlag
      this.isMAOverwritten = true
    }
    if (configs.lifeQuotesFlag) {
      this.lifeQuotesFlag = configs.lifeQuotesFlag
      this.isLIOverwritten = true
    }
    if (configs.pdpQuotesFlag) {
      this.pdpQuotesFlag = configs.pdpQuotesFlag
      this.isPDPverwritten = true
    }
  }

  fetchConfigs = async (): Promise<UsersABConfigType> => {
    Log.info(this, 'fetching AB Config')

    const result = await ApiClient.fetchABConfig()
    Log.info(this, `AB config response: ${result}`)
    Log.obj(result)

    if (result && result.error) {
      Log.error(this, `error fetching AB config: ${result.error}`)
      return result
    }

    Config.setABConfigs(result)

    const {
      uid: userId,
      lid: leadId,
      medsup_quotes,
      medadv_quotes,
      life_quotes,
      pdp_quotes
    } = result

    const config = { ...result, isDefault: false }
    Log.info(this, `user id: ${userId}`)
    Log.info(this, `lead id: ${leadId}`)

    Analytics.setUserId(userId)

    if (this.onSaveSessionData) {
      this.onSaveSessionData('userId', userId)
      this.onSaveSessionData('leadId', leadId)
      this.onSaveSessionData('abConfig', config)
    }

    Analytics.log('Set AB Config', result)

    this.medSupQuotesFlag = this.isMSOverwritten ? this.medSupQuotesFlag : medsup_quotes
    this.medAdvQuotesFlag = this.isMAOverwritten ? this.medAdvQuotesFlag : medadv_quotes
    this.lifeQuotesFlag = this.isLIOverwritten ? this.lifeQuotesFlag : life_quotes
    this.pdpQuotesFlag = this.isPDPverwritten ? this.pdpQuotesFlag : pdp_quotes
    this.isFetched = true
    this.configs = result

    return result
  }

  public async getQuoteConfigs(): Promise<ABQuotesFlags> {
    if (!this.isFetched) {
      await this.fetchConfigs()
    }

    return this.getQuoteConfigsSync()
  }

  public getQuoteConfigsSync(): ABQuotesFlags {
    return {
      medSupQuotesFlag: this.medSupQuotesFlag,
      medAdvQuotesFlag: this.medAdvQuotesFlag,
      lifeQuotesFlag: this.lifeQuotesFlag,
      pdpQuotesFlag: this.pdpQuotesFlag
    }
  }

  public async getConfigs(): Promise<UsersABConfigType> {
    if (!this.configs) {
      return this.fetchConfigs()
    }

    return this.configs
  }
}

export const ABConfigsStore = new ABConfigs()
